import { useInView } from '../../hooks/useInView';

interface AreaItem {
  city: string;
  note?: string;
}

interface SectionServiceAreasProps {
  heading?: string;
  description?: string;
  areas: AreaItem[];
}

export default function SectionServiceAreas({ heading, description, areas }: SectionServiceAreasProps) {
  const { ref: leftRef, isVisible: leftVisible } = useInView();
  const { ref: rightRef, isVisible: rightVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Text */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`slide-left ${leftVisible ? 'is-visible' : ''}`}
          >
            <div className="inline-flex items-center gap-4 mb-5">
              <span className="gold-divider" />
              <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Service Areas</p>
            </div>
            <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl mb-6 leading-tight">
              {heading || 'Serving the Greater Fort Worth Region'}
            </h2>
            <p className="text-rdr-muted font-sans text-base leading-relaxed mb-10 max-w-sm">
              {description ||
                'Red Door Ranch is conveniently located 20 minutes south of Fort Worth on Chisholm Trail Parkway — the ideal luxury wedding venue for couples across the DFW metroplex and surrounding communities.'}
            </p>
            <a
              href="https://www.google.com/maps/dir//5801+County+Rd+915,+Joshua,+TX+76058"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-3 text-rdr-charcoal font-sans text-xs tracking-[0.2em] uppercase border-b border-rdr-charcoal/30 pb-1 hover:text-rdr-gold hover:border-rdr-gold transition-colors duration-300 cursor-pointer"
            >
              Get Directions
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </a>
          </div>

          {/* Areas Grid */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`slide-right ${rightVisible ? 'is-visible' : ''}`}
          >
            <div className="grid grid-cols-2 gap-3">
              {areas.map((area, i) => (
                <div
                  key={i}
                  className={`bg-rdr-cream px-5 py-4 border border-rdr-cream-dark flex items-center gap-3 hover:border-rdr-gold/50 transition-colors duration-300 ${
                    area.note ? 'border-l-2 border-l-rdr-gold' : ''
                  }`}
                >
                  <div className="w-1.5 h-1.5 bg-rdr-gold rounded-full flex-shrink-0" />
                  <div>
                    <span className="font-serif text-rdr-charcoal text-base">{area.city}</span>
                    {area.note && (
                      <p className="text-rdr-gold text-xs font-sans mt-0.5">{area.note}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
